import {
  all, call, takeLatest, put, select
} from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import { Types as WaitingTicketsTypes } from './waitingTickets';
import { Types as UserTypes } from './user';
import { Types as TicketTypes } from './ticket';
import { Types as ModalServicesTypes } from './modalServices';
import { Types as ServicesTypes } from './services';
import { Types as ForwardingTypes } from './forwardingModal';
import { Types as TicketRatingsModalTypes } from './ticketRatingModal';

import customerService from '../services/customerService';
import ticketService from '../services/ticketService';
import userService from '../services/userService';
import lineService from '../services/lineService';

import getErrorMessage from '../utils/errorHandler';
import { setAuthToken, removeAuthToken } from '../utils/axiosAPI';
import {
  getCookie, removeCookie, setCookie, getUserSessionType
} from '../utils/cookie';

function* requestLogin(action) {
  try {
    const response = yield call(userService.requestLogin, action.payload.data);
    const body = response.data;

    if (body.status && body.status === 400) {
      toastr.error('', body.message);
      return;
    }

    setAuthToken(body);
    setCookie(body);

    yield put({ type: UserTypes.TOKEN_VALIDATED, payload: body });
    yield call(userService.successLogin);
    yield put({ type: UserTypes.SUCCESS_LOGIN });

    if (action.payload && action.payload.callback) {
      try {
        action.payload.callback();

        document.title = 'Code USER';
      } catch (err) {
        const error = `Houve um erro e a página será atualizada! ${getErrorMessage(err)}`;
        toastr.error('', error);
        setTimeout(() => {
          document.location.reload(true);
        }, 4000);
      }
    }
  } catch (error) {
    if (error.message === 'Network Error') {
      toastr.error('Erro de conexão com o servidor');
    } else if (error.response.status === 401) {
      toastr.error('Usuário/senha inválida');
    } else {
      toastr.error(getErrorMessage(error));
    }
  }
}

function* requestLogout(action) {
  try {
    const user = yield select((state) => state.user.data);

    yield call(userService.logout, user.workstation.id);
    yield put({ type: UserTypes.TOKEN_INVALIDATED });

    removeCookie();
    removeAuthToken();

    if (action.payload && action.payload.callback) {
      action.payload.callback();
    }

    document.title = 'Code USER';
  } catch (error) {
    toastr.error('Não foi possível deslogar do sistema', getErrorMessage(error));
  }
}

function* requestSelfInformations(action) {
  try {
    let response = yield call(userService.selfInformation);
    const selfInformations = response.data;

    if ([1, 2, 3, 4].includes(selfInformations.status)) {
      try {
        response = yield call(ticketService.ticketsInAttendanceByUser);
        const ticket = response.data;

        yield put({
          type: UserTypes.SUCCESS_SELF_INFORMATIONS,
          payload: selfInformations
        });

        if (ticket.id) {
          yield put({ type: TicketTypes.FETCHED_TICKET, payload: ticket });
          yield put({ type: UserTypes.HOLDING_TICKET, payload: true });
        }

        if (getUserSessionType() === 1) {
          document.title = `Code USER (${selfInformations.name})`;
        }
      } catch (error) {
        toastr.error('Não foi possível recuperar os dados da senha', getErrorMessage(error));
      }
    } else if ([5].includes(selfInformations.status)) {
      toastr.warning('Atenção', 'Você foi deslogado do sistema');
      yield put({ type: UserTypes.TOKEN_INVALIDATED });

      if (action.payload && action.payload.redirect) {
        action.payload.redirect();
      }
    } else {
      yield put({ type: UserTypes.SUCCESS_SELF_INFORMATIONS, payload: selfInformations });
    }
  } catch (error) {
    toastr.error('Não foi possível recuperar seus dados no servidor', getErrorMessage(error));
  }
}

function* requestTokenInformations(action) {
  const cookie = getCookie();

  yield put({
    type: UserTypes.SUCCESS_TOKEN_INFORMATIONS,
    payload: {
      token: cookie,
      hasValidToken: cookie && cookie !== null
    }
  });
  if (action && action.payload && action.payload.callback) {
    action.payload.callback();
  }
}

function* requestLoginWorkstation(action) {
  try {
    const { workstation, environmentName } = action.payload;

    const response = yield call(userService.loginWorkstation, workstation.id);

    if (response.data.status === 500) {
      yield put({ type: UserTypes.FAILURE_LOGIN_WORKSTATION });

      toastr.error('Não foi possível logar na estação', getErrorMessage(response.data));

      return;
    }

    yield put({
      type: UserTypes.SUCCESS_LOGIN_WORKSTATION,
      payload: {
        environmentName,
        workstation
      }
    });
  } catch (error) {
    yield put({ type: UserTypes.FAILURE_LOGIN_WORKSTATION });
    toastr.error('Não foi possível logar na estação', getErrorMessage(error));
  }
}

function* requestWaitingTickets() {
  try {
    const user = yield select((state) => state.user.data);
    const response = yield call(ticketService.waiting, user.workstation.id);

    yield put({ type: WaitingTicketsTypes.SUCCESS_WAITING_TICKETS, payload: response.data });
  } catch (err) {
    toastr.error('Erro ao consultar senhas em espera', getErrorMessage(err));
    yield put({ type: WaitingTicketsTypes.FAILURE_WAITING_TICKETS });
  }
}

function* requestCallNextTicket(action) {
  try {
    const user = yield select((state) => state.user.data);
    const response = yield call(ticketService.callNext, user.workstation.id);

    if (!response.data.id) {
      toastr.warning('Nenhuma senha em espera!');
      return;
    }

    yield put({ type: TicketTypes.FETCHED_TICKET, payload: response.data });
    yield put({ type: UserTypes.HOLDING_TICKET, payload: true });

    if (action.payload && action.payload.callback) {
      action.payload.callback();
    }

    toastr.info('', 'Senha chamada');
  } catch (error) {
    toastr.error('Não foi possível chamar a senha!', getErrorMessage(error));
  } finally {
    if (action.payload && action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function* requestCancelTicket(action) {
  const { data, onComplete, onSuccess } = action.payload;
  try {
    const user = yield select((state) => state.user.data);
    const ticket = {
      ...data,
      workstationId: user.workstation.id
    };

    yield call(ticketService.cancel, ticket);
    yield put({ type: TicketTypes.UNATTACH_TICKET });
    yield put({ type: UserTypes.HOLDING_TICKET, payload: false });
    yield put({ type: ServicesTypes.REMOVE_SELECTION_SERVICES });

    toastr.info('', 'Senha cancelada!');

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    toastr.error('Não foi possível cancelar a senha', getErrorMessage(error));
  } finally {
    if (onComplete) {
      onComplete();
    }
  }
}

function* requestCallTicket(action) {
  try {
    let priority = 0;
    let ticket = null;
    let callback = () => { };

    if (action.payload) {
      priority = action.payload.priority;
      ticket = action.payload.ticket;
      callback = action.payload.callback;
    } else {
      ticket = yield select((state) => state.ticket);
    }

    const user = yield select((state) => state.user.data);

    const data = {
      ticket: {
        id: ticket.id,
        label: ticket.label,
        ratingColor: ticket.ratingColor,
        ticketInformation: ticket.ticketInformation
      },
      workstation: {
        id: user.workstation.id,
        number: user.workstation.number,
        description: user.workstation.name
      },
      line: {
        id: ticket.line.id,
        name: ticket.line.descriptionOnPanel,
        color: ticket.line.color
      },
      priority
    };

    yield call(ticketService.call, data);
    yield put({ type: TicketTypes.SUCCESS_CALL_TICKET });

    callback();

    toastr.info('', 'Senha chamada');
  } catch (error) {
    toastr.error('Não foi possível chamar a senha', getErrorMessage(error));
  }
}

function* requestStartAttendance(action) {
  try {
    let priority = 0;
    let ticket = null;
    let callback = () => { };

    if (action.payload && action.payload.ticket && action.payload.priority
      && action.payload.callback) {
      priority = action.payload.priority;
      ticket = action.payload.ticket;
      callback = action.payload.callback;
    } else {
      ticket = yield select((state) => state.ticket);
    }

    const user = yield select((state) => state.user.data);

    const data = {
      ticketId: ticket.id,
      lineId: ticket.line.id,
      workstationId: user.workstation.id,
      priority
    };

    yield call(ticketService.startAttendance, data);
    yield put({ type: TicketTypes.SUCCESS_START_ATTENDANCE });

    callback();

    toastr.info('', 'Atendimento iniciado');
  } catch (error) {
    toastr.error('Não foi possível iniciar o atendimento!', getErrorMessage(error));
  } finally {
    if (action.payload && action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function validateSelectedServices(modalServices, allGroupServices) {
  const notSelectedGroupServices = [];

  allGroupServices.forEach((gs) => {
    if (gs.required) {
      let notSelected = true;
      const selectedGroupServices = modalServices.selection;

      selectedGroupServices.forEach((sgs) => {
        if (sgs.groupServiceId === gs.id) {
          notSelected = false;
        }
      });

      if (notSelected) {
        notSelectedGroupServices.push(gs.name);
      }
    }
  });

  if (notSelectedGroupServices.length > 0) {
    return {
      cause: `Informe um ou mais serviços para os grupos: ${notSelectedGroupServices.join(', ')}`,
      message: 'Atenção',
      type: 'warning',
    };
  }

  return {
    type: 'success'
  };
}

function* requestDoneAttendance(action) {
  try {
    const services = yield select((state) => state.services);
    const { fetchedGroupServices } = action.payload;

    const validationResult = validateSelectedServices(services, fetchedGroupServices);

    if (['error', 'warning'].includes(validationResult.type)) {
      if (validationResult.type === 'warning') {
        toastr.warning(validationResult.message, validationResult.cause);
        return;
      }
      if (validationResult.type === 'error') {
        toastr.error(validationResult.message, validationResult.cause);
        return;
      }
    }

    const ticket = yield select((state) => state.ticket);
    const user = yield select((state) => state.user.data);

    const data = {
      ticketId: ticket.id,
      lineId: ticket.line.id,
      workstationId: user.workstation.id,
      surveyId: ticket.line.hasSurvey === 'T' ? ticket.line.survey.id : null,
      surveyViaSms: ticket.line.hasSurvey === 'T' ? ticket.line.survey.surveyViaSms : 'F',
      selectedGroupServiceList: services.selection,
      customerPhone: ticket.customer.phone1,
    };

    yield call(ticketService.doneAttendance, data);

    yield put({ type: TicketTypes.SUCCESS_DONE_ATTENDANCE });
    yield put({ type: UserTypes.HOLDING_TICKET, payload: false });
    yield put({ type: ModalServicesTypes.SHOW_MODAL_SERVICES, payload: false });
    yield put({ type: ServicesTypes.RESET });

    toastr.info('', 'Atendimento finalizado!');
  } catch (error) {
    toastr.error('Não foi possível finalizar o atendimento!', getErrorMessage(error));
  } finally {
    if (action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function* requestFetchServices(action) {
  const { groupServices } = action.payload;
  yield put({
    type: ServicesTypes.SUCCESS_FETCH_SERVICES,
    payload: {
      groupServices
    }
  });
  yield put({ type: ModalServicesTypes.SHOW_MODAL_SERVICES, payload: true });
}

function* requestSelectServices(action) {
  const { groupServiceId, isRequired, services } = action.payload;

  yield put({
    type: ServicesTypes.SUCCESS_SELECT_SERVICES,
    payload: {
      groupServiceId,
      isRequired,
      services
    }
  });
}

function* requestSaveTicketDescription(action) {
  try {
    const ticket = yield select((state) => state.ticket);

    yield call(ticketService.saveDescription, ticket.id, action.payload);
    yield put({
      type: TicketTypes.SUCCESS_SAVE_TICKET_DESCRIPTION,
      payload: action.payload
    });

    toastr.info('Comentários salvos com sucesso!');
  } catch (err) {
    toastr.error('Não foi possível salvar o comentário!', getErrorMessage(err));
  }
}

function* requestSaveCustomer(action) {
  try {
    // Extrai os dados do cliente da ação recebida
    const customer = action.payload;

    // Verifica se todos os dados necessários do cliente estão presentes
    if (!customer.document || !customer.name || !customer.category) {
      // Se algum dado estiver faltando, exibe uma mensagem de erro
      toastr.error('Erro', 'Informe todos os dados do cliente e IDs de ticket e cliente!');
      return;
    }

    // Obtém o ticket atual do estado da aplicação
    const ticket = yield select((state) => state.ticket);

    // Faz uma chamada para salvar os dados do cliente
    const response = yield call(customerService.save, customer);

    // vincula a senha com o cliente
    yield call(ticketService.attachCustomer, ticket.id, response.data.id);

    // Atualiza o estado da aplicação para refletir o cliente que foi salvo
    yield put({ type: TicketTypes.ATTACH_CUSTOMER, payload: customer });

    // Exibe uma mensagem de sucesso após salvar o cliente
    toastr.info('Cliente salvo com sucesso!');

    // Faz a associação entre o ticket atual e o cliente no estado da aplicação, independentemente de o cliente já estar vinculado a outra senha ou não
    yield put({ type: TicketTypes.ATTACH_CUSTOMER, payload: { ticketId: ticket.id, customerId: response.data.id } });

  } catch (err) {
    // Se ocorrer um erro durante o processo, exibe uma mensagem de erro
    toastr.error('Não foi possível salvar os dados do cliente!', getErrorMessage(err));
  }
}

function* requestForwardingList() {
  try {
    const ticket = yield select((state) => state.ticket);

    const response = yield call(lineService.getForwardingList, ticket.line.id);
    const json = response.data;

    if (!json.lineForwardingResponseList || json.lineForwardingResponseList.length === 0) {
      toastr.warning('', 'Não existem filas cadastradas para encaminhamento');
      return;
    }

    yield put({
      type: ForwardingTypes.SUCCESS_FORWARDING_LIST,
      payload: {
        lines: json.lineForwardingResponseList,
        groupServices: json.lineGroupServiceResponseList,
      }
    });

    if (json.lineGroupServiceResponseList && json.lineGroupServiceResponseList.length > 0) {
      yield put({
        type: ServicesTypes.SUCCESS_FETCH_SERVICES,
        payload: {
          groupServices: json.lineGroupServiceResponseList
        }
      });
    }
  } catch (error) {
    toastr.error('Erro ao carregar filas para encaminhamento', getErrorMessage(error));
  }
}

function* requestForward(action) {
  try {
    const services = yield select((state) => state.services);
    const { fetchedGroupServices } = action.payload;

    const validationResult = validateSelectedServices(services, fetchedGroupServices);

    if (['error', 'warning'].includes(validationResult.type)) {
      if (validationResult.type === 'warning') {
        toastr.warning(validationResult.message, validationResult.cause);
        return;
      }
      if (validationResult.type === 'error') {
        toastr.error(validationResult.message, validationResult.cause);
        return;
      }
    }

    const ticket = yield select((state) => state.ticket);
    const user = yield select((state) => state.user.data);

    yield call(ticketService.forward, {
      hasPriorityOnRating: action.payload.hasPriorityOnRating,
      lineId: ticket.line.id,
      targetLineId: action.payload.targetLineId,
      ticketId: ticket.id,
      ratingId: action.payload.ratingId == 0 ? null : action.payload.ratingId,
      selectedGroupServiceList: services.selection,
      workstationId: user.workstation.id,
    });

    if (action.payload.callback) {
      action.payload.callback();
    }
    yield put({ type: ForwardingTypes.SUCCESS_FORWARD });
    yield put({ type: TicketTypes.SUCCESS_DONE_ATTENDANCE });
    yield put({ type: UserTypes.HOLDING_TICKET, payload: false });
    yield put({ type: ServicesTypes.RESET });

    toastr.info('', 'Senha encaminhada com sucesso!');
  } catch (error) {
    toastr.error('Erro ao encaminhar senha', getErrorMessage(error));
  } finally {
    if (action.payload && action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function* requestRetriveTicketRatings() {
  try {
    const response = yield call(ticketService.getTicketRatings);
    const ratings = response.data;

    if (ratings.length === 0) {
      toastr.warning('', 'Não existem classificações cadastradas');
      return;
    }

    yield put({
      type: TicketRatingsModalTypes.SUCCESS_RETRIEVE_TICKET_RATINGS,
      payload: ratings
    });
  } catch (error) {
    toastr.error('Erro ao consultar classificações', getErrorMessage(error));
  }
}

function* requestChangeTicketRating(action) {
  try {
    const ticket = yield select((state) => state.ticket);
    const user = yield select((state) => state.user.data);

    yield call(ticketService.changeTicketRating, {
      lineId: ticket.line.id,
      ticketId: ticket.id,
      ratingId: action.payload.ratingId,
      userId: user.id,
      workstationId: user.workstation.id
    });

    if (action.payload.callback) {
      action.payload.callback();
    }
    yield put({ type: TicketRatingsModalTypes.HIDE_MODAL });
    yield put({ type: TicketTypes.SUCCESS_DONE_ATTENDANCE });
    yield put({ type: UserTypes.HOLDING_TICKET, payload: false });

    toastr.info('', 'Classificação alterada com sucesso!');
  } catch (error) {
    toastr.error('Erro ao alterar classificação', getErrorMessage(error));
  } finally {
    if (action.payload.onComplete) {
      action.payload.onComplete();
    }
  }
}

function* requestFindTicketById(action) {
  try {
    const response = yield call(ticketService.findTicketById, action.payload.ticketId);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response.data);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

export default function* root() {
  yield all([
    takeLatest(WaitingTicketsTypes.REQUEST_WAITING_TICKETS, requestWaitingTickets),
    takeLatest(UserTypes.REQUEST_SELF_INFORMATIONS, requestSelfInformations),
    takeLatest(UserTypes.REQUEST_LOGIN, requestLogin),
    takeLatest(UserTypes.REQUEST_LOGOUT, requestLogout),
    takeLatest(UserTypes.REQUEST_LOGIN_WORKSTATION, requestLoginWorkstation),
    takeLatest(TicketTypes.REQUEST_CALL_NEXT_TICKET, requestCallNextTicket),
    takeLatest(TicketTypes.REQUEST_CANCEL_TICKET, requestCancelTicket),
    takeLatest(TicketTypes.REQUEST_CALL_TICKET, requestCallTicket),
    takeLatest(TicketTypes.REQUEST_START_ATTENDANCE, requestStartAttendance),
    takeLatest(TicketTypes.REQUEST_DONE_ATTENDANCE, requestDoneAttendance),
    takeLatest(ServicesTypes.REQUEST_FETCH_SERVICES, requestFetchServices),
    takeLatest(ServicesTypes.REQUEST_SELECT_SERVICES, requestSelectServices),
    takeLatest(TicketTypes.REQUEST_SAVE_TICKET_DESCRIPTION, requestSaveTicketDescription),
    takeLatest(TicketTypes.REQUEST_SAVE_CUSTOMER, requestSaveCustomer),
    takeLatest(ForwardingTypes.REQUEST_FORWARDING_LIST, requestForwardingList),
    takeLatest(ForwardingTypes.REQUEST_FORWARD, requestForward),
    takeLatest(TicketRatingsModalTypes.REQUEST_RETRIEVE_TICKET_RATINGS, requestRetriveTicketRatings),
    takeLatest(TicketTypes.REQUEST_CHANGE_TICKET_RATING, requestChangeTicketRating),
    takeLatest(UserTypes.REQUEST_TOKEN_INFORMATIONS, requestTokenInformations),
    takeLatest(TicketTypes.REQUEST_FIND_TICKET_BY_ID, requestFindTicketById),
  ]);
}
